import { ChangeDetectorRef, Component, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import {v4 as uuidv4} from 'uuid';

import { environment } from "../environments/environment"
import { AuthService } from './commons/_services/auth.service';
import { MechanicsService } from './commons/_services/mechanics.service';
import { OptionsListService } from './commons/_services/options-list.service';
import { PreferencesService } from './commons/_services/preferences.service';
import { QueryParamsService } from './commons/_services/queryParams.service';
import { RecentSearchesService } from './commons/_services/recentSearches.service';
import { ReportsService } from './commons/_services/reports.service';
import { SearchService } from './commons/_services/search.service';
import { buildCaptchaUrl, CaptchaService } from './commons/_services/captcha.service';
import { DBNews } from './commons/interfaces';
import { HttpClient } from '@angular/common/http';
import { instanceType } from './commons/utils';


declare const SmartlingContextTracker

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AppComponent {

	public environment = environment
	
	public logsSettings: any = {
		isExists: false,
		align: "left",
		minimize: false
	}
	public hashSearches: string = localStorage.getItem(`${instanceType()}.hashSearches`)

	public helpOptions: any[] = [];
	public helpMore: any[] = [];
	public isOpen = true;
	public is_news_closed: boolean = false
	public readonly isFeedbackWidgetEnabled = instanceType() === 'gbd';
	public isFeedbackWidgetDismissed = false;
	private readonly feedbackSurveys: Record<string, { label: string; text: string; url: string }> = {
		en: {
			label: 'Take the survey',
			text: 'Annual user survey — 7 questions, about 3 minutes. Open until 30 November.',
			url: 'Global-Brand-Database-Survey-2026-english'
		},
		fr: {
			label: "Répondre à l'enquête",
			text: "Enquête annuelle — 7 questions, 3 minutes. Jusqu'au 30 novembre.",
			url: 'Global-Brand-Database-Survey-2026-french'
		},
		es: {
			label: 'Responder a la encuesta',
			text: 'Encuesta anual — 7 preguntas, 3 minutos. Hasta el 30 de noviembre.',
			url: 'Global-Brand-Database-Survey-2026-spanish'
		}
	};

	constructor(
		private router: Router,
		public ms: MechanicsService,
		public ss: SearchService,
		public rss: RecentSearchesService,
		public ps: PreferencesService,
		public qs: QueryParamsService,
		public rs: ReportsService,
		private authService: AuthService,
		public ols:OptionsListService,
		public http: HttpClient,
		public captchaService: CaptchaService,
		private changeDetector: ChangeDetectorRef) {

		this.logsSettings.isExists = !this.ms.isAwsProd;
		
		// Make captcha service available globally for debugging
		if (typeof window !== 'undefined') {
			(window as any).captchaService = this.captchaService;
		}
	}

	select_with_lang(value){
		return value ? (value[this.ms.lang] || value["en"]) : '';
	}

	get feedbackSurvey(): { label: string; text: string; url: string } {
		return this.feedbackSurveys[this.ms.lang] || this.feedbackSurveys.en;
	}

	get shouldShowFeedbackWidget(): boolean {
		const path = this.router.url.split(/[?#]/, 1)[0];
		const segments = path.split('/').filter(Boolean);
		return this.isFeedbackWidgetEnabled
			&& segments[segments.length - 1] === 'similarname'
			&& !this.isFeedbackWidgetDismissed;
	}

	dismissFeedbackWidget(): void {
		this.isFeedbackWidgetDismissed = true;
	}

	loadWipoFeedbackWidget(): void {
		if (!this.isFeedbackWidgetEnabled) {
			return;
		}

		const script = document.createElement('script');
		script.type = 'module';
		script.src = this.ms.isLocalHost
			? '/feedback-widget/components/wu-alchemer-widget.js'
			: 'https://web.cdnjs.webcms.acc.web1.wipo.int/universal-lf-webcomponents/latest/components/wu-alchemer-widget.js';
		script.async = true;
		script.onload = () => this.styleFeedbackDialog();
		script.onerror = () => console.error('Failed to load the WIPO feedback widget.');
		document.head.appendChild(script);
	}

	private styleFeedbackDialog(): void {
		const attachClickHandler = (): boolean => {
			const widget = document.querySelector<HTMLElement>('wu-alchemer-widget');
			if (!widget?.shadowRoot) {
				return false;
			}

			widget.addEventListener('click', () => this.styleFeedbackDialogCloseButton(widget), { once: true });
			return true;
		};

		if (!attachClickHandler()) {
			const observer = new MutationObserver(() => {
				if (attachClickHandler()) {
					observer.disconnect();
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });
		}
	}

	private styleFeedbackDialogCloseButton(widget: HTMLElement): void {
		const widgetShadow = widget.shadowRoot;
		if (!widgetShadow) {
			console.error('Unable to access the WIPO feedback widget shadow root.');
			return;
		}

		const installCloseStyle = (): boolean => {
			const dialogShadow = widgetShadow.querySelector<HTMLElement>('wu-dialog')?.shadowRoot;
			if (!dialogShadow) {
				return false;
			}

			if (!dialogShadow.querySelector('[data-gbd-feedback-dialog]')) {
				const style = document.createElement('style');
				style.dataset.gbdFeedbackDialog = 'true';
				style.textContent = '.wu-dialog__close { font-size: 0 !important; } .wu-dialog__close::after { content: "×"; font: 24px/1 sans-serif; }';
				dialogShadow.appendChild(style);
			}
			return true;
		};

		if (installCloseStyle()) {
			return;
		}

		let attemptsRemaining = 20;
		const retryInstall = (): void => {
			if (installCloseStyle()) {
				return;
			}
			if (attemptsRemaining-- > 0) {
				window.setTimeout(retryInstall, 50);
			} else {
				console.error('Unable to access the WIPO survey dialog shadow root.');
			}
		};
		retryInstall();
	}

	get_news_for_lang(): string {
		if (this.ms.news.length == 0)
			return;

		let one_week_ago = new Date(Date.now() - 604800000);
		if (new Date(this.ms.news[0].news_date) < one_week_ago) {
			this.ms.show_news = false
			return;
		}
		return `<i>${this.ms.news[0].news_date_human}</i>:&nbsp;&nbsp;<b>${this.select_with_lang(this.ms.news[0].title)}</b> - ${this.select_with_lang(this.ms.news[0].payload)}`
	}

	async getNews() {

		const l: string = `quickSearchComponent getNews() - `;

		try {
			let news: DBNews[] = await this.ss.getDbNews();

			// PL: fix here for keeping the original "news_date" to be used in get_news_for_lang()
			this.ms.news = news.slice(0, 1).map(n => {
				n["news_date_human"] = this.ms.dateFormatterHuman.format(new Date(n["news_date"]));
				return n;
			})
		}
		catch (err) {
			// console.log(`${l}Caught error : `, err)
		}
	}

	async ngOnInit() {
		this.ngOnInitNoBot()
		/*
		load()
			.then((botd) => botd.detect())
			.then((result) => {
				if(result.bot == false)
				else{
					alert("You have been identified as an automated connection, which is against the Terms of Use of the application.")
				}
			})
			.catch((error) => {
				alert("You have been identified as an automated connection, which is against the Terms of Use of the application.")
				console.error(error)
			});
		*/
	}

	// hcaptcha

	async onVerify(ev: any) {
		if (ev.type === 'verified') {
			let token = ev.detail.payload
			this.hashSearches = uuidv4()
			localStorage.setItem(`${instanceType()}.hashSearches`, this.hashSearches)
			let res = await this.ols.getDbInfo({token})
			if(res){
				window.location.reload();
			}
		}
	}		
	
	onExpired(response: any) {
		// The verification expired.
	}
	
	onError(error: any) {
		// An error occured during the verification process.
	}

	async ngOnInitNoBot(){
	
		const l = `app.component ngOnInit() - `

		//if (this.ms.isAwsAcc) this.loadSmartlingContext() // only on AWS acceptance

		let state: string = window.location.pathname + window.location.search;

		this.buildHelpMenu();

		// loading nav bar before doSSO() - PL: it was initially loaded after, but it appeared to trigger 
		// useless redundant SSO 
		this.loadWipoNavbar();
		this.loadWipoFeedbackWidget();

		//if (!this.authService.hasValidToken)
		state = await this.authService.doSSO(); 

		// do fake captcha request for auth users
		const hashSearches = localStorage.getItem(`${instanceType()}.hashSearches`);
		if (this.captchaService.isCaptchaEnabled && hashSearches) {
			const route = buildCaptchaUrl(environment[instanceType()].backendUrl || "", hashSearches);
			this.http.get(route).subscribe();
		}
		let oldLocation = localStorage.getItem(`${instanceType()}.lggedFrom`)
		if (oldLocation) {
			localStorage.removeItem(`${instanceType()}.lggedFrom`)
			window.location.href = oldLocation;
		}

		const recentSearches: string = localStorage.getItem(`${instanceType()}.recentSearches`)
		if (recentSearches) {
			let evtDetail = { detail: {"value":1,"text": instanceType() + "_returing_visitor"}}
			let evt = new CustomEvent("wipoPing", evtDetail );
			document.dispatchEvent(evt);
		}
		// console.log(`${l}state=`, state)

		// this must be done after doSSO()
		if (this.authService.hasValidToken) {
			try {
				console.log(`${l}loadng saved searches`);
				await this.rss.loadSavedSearches();
			} catch (err) {
				console.log(`${l}Could not load saved searches - Full err =`, err)
			}
		}

		try {
			console.log(`${l}loading preferences`);
			await this.ps.loadPreferences(this.authService.hasValidToken);

			// we also need to set the rows parameter in the URL, otherwise the first next search will
			// take the URL parameter value rather than the prefered one
			this.qs.setQP("rows", this.ps.preferences["similarname"]["rows"], "similarname");
			this.qs.setQP("rows", this.ps.preferences["similarlogo"]["rows"], "similarlogo");
			this.qs.setQP("rows", this.ps.preferences["advancedsearch"]["rows"], "advancedsearch");
		} catch (err) {
			console.log(`${l}Could not load preferences - Full err =`, err)
		}

		this.router.navigateByUrl(state, {
			skipLocationChange: false,
			replaceUrl: true
		})

		const logsSettingsString = localStorage.getItem(instanceType()+".logsSettings");

		if (logsSettingsString) {
			try {
				let logsSettings = JSON.parse(logsSettingsString);
				this.logsSettings = Object.assign(this.logsSettings, logsSettings)
				// console.log(`${l}this.logsSettings=`,this.logsSettings)

			} catch (err) {
				// console.log(`${l}Could not restore logs settings, ignoring - Full err =`, err)
			}
		}
		await this.getNews();
	}

	ngAfterViewChecked() {

		/*
			While in development, the console can get filled with very annoying "error" messages saying that some variable has changed since last time it was checked (??)
			Some Angular weird stuff.
			
			Apparently, calling changeDetector.detectChanges() prevents those.
			
			It doesn't happen in prod build, so I'm only doing it in dev


			--> Update 2022-11-03 : Extremely tenacious bug I've spent hours to chase.
			on localhost and awsDev, all was working fine. But in acc and uat, we were only getting blank pages and unrefreshed templates. Uncommenting this and leaving changeDetector.detectChanges() in all environment seems to fix the problem. So LEAVE THIS ON!
		*/

		this.changeDetector.detectChanges();
	}

	public getPageCoverageTitle(): string {
		let title = this.ms.translate("page_coverage.data_coverage")
		
		/*
		if (this.ms.officeCC) {
			title = `${title} - ${this.ms.officeCC}`
		}*/

		return title
	}

	async buildHelpMenu() {

		const l = `appcomponent buildHelpMenu() - `
		let antiloop = 0
		// console.log(`${l}Rebuilding help menu`)
		while (!this.ms.translations && antiloop < 50) {
			await new Promise(r => setTimeout(r, 200));
			antiloop++;
		}
		

		this.helpOptions = [  // Optional, set custom links for Contact and FAQ  (default = WIPO CONTACT)
			{
				"code": "contact", // - code property is mandatory
				"link": `https://www3.wipo.int/contact/${this.ms.lang}/area.jsp?area=branddb` // - link property is optional
			},
			{
				"name": "FAQs",
				"code": "faq",
				"link": environment[instanceType()].FAQUrl.replace("{lang}", this.ms.lang),
				"targetTab": "_self"
			}
		];

		this.helpMore = [
			{
				"name": this.getPageCoverageTitle(),
				"code": "coverage",
				"link": this.ms.makeRoute({ path: 'coverage', subpath: '', includeOffice: false }),
				"targetTab": "_self"
			},
			{
				"name": this.ms.translate("help_menu.news"),
				"code": "news",
				"link": `${environment[instanceType()].appUrl}/${this.ms.lang}/news`,
				"targetTab": "_self"
			},
			{
				"name": this.ms.translate("help_menu.user_reference_guide"),
				"code": "guide",
				"link": `${environment[instanceType()].appUrl}/assets/gbd_doc.pdf`,
				"targetTab": "_self"
			},

			{
				"name": this.ms.translate("help_menu.terms_of_use"),
				"code": "terms",
				"link": `https://www.wipo.int/reference/${this.ms.lang}/branddb/terms_and_conditions.html`,
				"targetTab": "_self"
			},

		];
	}

	loadWipoNavbar(): void {

		const l = `appcomponent loadWipoNavbar() - `
		let counter = 0;

		const wipoComponentsPath: string = environment[instanceType()].wipoComponentsPath;
		const scripts = [
			`${wipoComponentsPath}/polyfills/webcomponents-loader.js`,
			`${wipoComponentsPath}/wipo-init/wipo-init.js`,
			`${wipoComponentsPath}/wipo-navbar/wipo-navbar.js`
		]
		if (!wipoComponentsPath) {
			// console.log(`${l}No wipoComponentsPath, not loading the WIPO navbar`)
			return
		}

		// console.log(`${l}wipoComponentsPath : `, wipoComponentsPath);

		scripts.forEach(src => {
			document.head.appendChild(Object.assign(document.createElement('script'), {
				src,
				async: true,
				onload: async () => {
					counter += 1;
					let waitForPing = true
					let retryies = 0
					if (counter == scripts.length){
						while(waitForPing && retryies < 100){
							retryies += 1
							await new Promise(r => setTimeout(r, 100))
							Array.from(document.head.getElementsByTagName("*")).forEach(element => {
								if(element.nodeName == 'LINK'){
									if(element.getAttribute('href').includes('noto-sans-kr-weights.css'))
										waitForPing = false
								}
							});
						}
						this.sendPingToAnalytics()
					}

				}
			}));
		});
	};

	sendPingToAnalytics(){
		const recentSearches: string = localStorage.getItem(instanceType()+".recentSearches")
		if (recentSearches){
			let evtDetail = { detail: {"value":1,"text":instanceType()+"_returning_visitor"}}
			let evt = new CustomEvent("wipoPing", evtDetail );
			document.dispatchEvent(evt);
		}	
	}
	
	/*loadSmartlingContext() {
		var script = document.createElement('script');
		script.onload = function () {
			SmartlingContextTracker.init({
				orgId: 'LR3HwdNcfBVS269bS54AUQ',
				host: 'api.smartling.com'
			});
		};
		script.src = "//d2c7xlmseob604.cloudfront.net/tracker.min.js";
		document.getElementsByTagName('head')[0].appendChild(script);
	}*/

	get windowWidth(): number {
		return window.innerWidth
	}

	setLogsSetting(key: string, value: any) {

		const l = `appComponent setLogsSetting() - `

		// console.log(`${l}setting logsSettings[${key}] = ${value}`)

		this.logsSettings[key] = value;

		localStorage.setItem(instanceType()+".logsSettings", JSON.stringify(this.logsSettings))
	}

	toggleDummyLogos($event) {

		const l = `compResultsInfo toggleDummyLogos() - `

		const isChecked: boolean = $event.target.checked

		// console.log(`${l}isCheckd = `, isChecked)

		localStorage.setItem("showDummyImages", "" + isChecked)

	}

	get isDummyLogos() {
		return localStorage.getItem("showDummyImages") === "true"
	}
}
